import React, { useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import Select from 'react-select';
import DotLoader from '../../components/DotLoader';
import ScrollToggle from '../../components/ScrollToggle/ScrollToggle';
import { API_URLS, MONTHS } from '../../resources/constants';
import { fileDownloadBlob, lastYears } from '../../resources/functions';
import { currencyFormat } from '../../resources/helper';
import { CallApi } from '../../saga/callApi';
import { getBranch } from '../../store/user/selectors';

interface MonthData {
    month_label: string;
    actual: number;
    budget: number;
    over_budget: number;
    pct_of_budget: number;
}

interface BvaRow {
    account_chart: {
        id: number;
        account: number;
        name: string;
        report_code: string;
    };
    months: MonthData[];
}

const BudgetVsActual: React.FC = () => {
    const { t } = useTranslation('scenes.BudgetVsActual.index');
    const branchData = useSelector(getBranch);
    const branchId = +branchData['id'];

    const currentYear = new Date().getFullYear();

    const [searchParams, setSearchParams] = useState<any>({
        year: { value: currentYear, label: String(currentYear) },
        from_month: { value: '01', label: t('month.01', { ns: 'resources.form-validator' }) },
        to_month: { value: '12', label:t('month.12',{ ns: 'resources.form-validator'}) },
    });
    const [reportData, setReportData] = useState<BvaRow[]>([]);
    const [monthLabels, setMonthLabels] = useState<string[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [hasData, setHasData] = useState(false);

    const tableContainerRef = useRef<HTMLDivElement>(null);

    const formatAmount = (value: number) => {
        const fixed = Math.abs(value).toFixed(2);
        return value < 0 ? `(${currencyFormat(fixed)})` : currencyFormat((value).toFixed(2));
    };

    const formatPct = (value: number) => `${value.toFixed(2)}%`;

    const generateReport = () => {
        const year = searchParams.year.value;
        const fromMonth = +searchParams.from_month.value;
        const toMonth = +searchParams.to_month.value;

        if (fromMonth > toMonth) {
            toast.error(t('msg.invalidMonthRange'));
            return;
        }

        const qs = new URLSearchParams({
            year: String(year),
            from_month: String(fromMonth),
            to_month: String(toMonth),
            branch_id: String(branchId),
        }).toString();

        setIsLoading(true);
        setHasData(false);
        setReportData([]);

        CallApi.GET(`${API_URLS.BUDGET.BUDGET_VS_ACTUAL}?${qs}`, {}, true)
            .then((resp: any) => {
                const data = resp?.data?.data;
                if (data && data.result && data.result.length > 0) {
                    setReportData(data.result);
                    setMonthLabels(data.month_labels ?? []);
                    setHasData(true);
                } else {
                    setReportData([]);
                    setMonthLabels([]);
                    setHasData(false);
                    toast.error(t('msg.noData'));
                }
            })
            .catch((err: any) => {
                const message = err?.data?.message || err?.response?.data?.message || t('msg.error');
                toast.error(message);
            })
            .finally(() => setIsLoading(false));
    };

    const downloadPdf = () => {
        const year = searchParams.year.value;
        const fromMonth = +searchParams.from_month.value;
        const toMonth = +searchParams.to_month.value;

        const qs = new URLSearchParams({
            year: String(year),
            from_month: String(fromMonth),
            to_month: String(toMonth),
            branch_id: String(branchId),
        }).toString();

        setIsLoading(true);
        CallApi.DOWNLOAD(`${API_URLS.BUDGET.BUDGET_VS_ACTUAL_REPORT}?${qs}`, {}, true)
            .then((resp: any) => {
                fileDownloadBlob(resp, `budget-vs-actual-${year}-${String(fromMonth).padStart(2, '0')}-${String(toMonth).padStart(2, '0')}`);
            })
            .catch((err: any) => {
                const message = err?.response?.data?.message || t('msg.downloadError');
                toast.error(message);
            })
            .finally(() => setIsLoading(false));
    };

    const colSpanCount = monthLabels.length * 4;

    return (
        <div className="main-container flex-grow-1">
            <div className="container-fluid">
                 <div className="d-flex justify-content-between align-items-center flex-wrap pb-3 pt-2">
                      <div>
                            <h1 className="h5 font-weight-700 mb-0 d-inline-flex text-black">{t('page.title')}</h1>
                        </div>
                        <div className="d-flex align-items-center gap-2 flex-wrap">
                                <div className="filter-box">
                                    <label className="form-label">{t('filter.year')}</label>
                                    <Select
                                        onChange={(e: any) => setSearchParams({ ...searchParams, year: e })}
                                        options={lastYears(5)}
                                        value={searchParams.year}
                                        className="zindex-10"
                                    />
                                </div>
                                <div className="filter-box">
                                    <label className="form-label">{t('filter.fromMonth')}</label>
                                    <Select
                                        onChange={(e: any) => setSearchParams({ ...searchParams, from_month: e })}
                                        options={MONTHS}
                                        value={searchParams.from_month}
                                        className="zindex-10"
                                    />
                                </div>
                                <div className="filter-box">
                                    <label className="form-label">{t('filter.toMonth')}</label>
                                    <Select
                                        onChange={(e: any) => setSearchParams({ ...searchParams, to_month: e })}
                                        options={MONTHS}
                                        value={searchParams.to_month}
                                        className="zindex-10"
                                    />
                                </div>
                                <div className="filter-box" style={{ alignSelf: 'flex-end' }}>
                                    <button
                                        type="button"
                                        onClick={generateReport}
                                        className="btn-primary btn p-2"
                                    >
                                        {t('filter.generate')} &nbsp;
                                        <i className="fa fa-search" aria-hidden="true"></i>
                                    </button>
                                </div>
                                {hasData && (
                                    <div className="filter-box text-end" style={{ alignSelf: 'flex-end' }}>
                                        <button
                                            type="button"
                                            onClick={downloadPdf}
                                            className="btn-dark btn"
                                        >
                                            {t('filter.exportPdf')} &nbsp;
                                            <i className="fa fa-download" aria-hidden="true"></i>
                                        </button>
                                    </div>
                                )}
                                <div className="filter-btn">
                                    <div className={`page-loader ${isLoading ? 'visible' : ''}`}>
                                        <div className="loader"></div>
                                    </div>
                                    {isLoading && <DotLoader text={t('filter.loading')} />}
                                </div>
                            
                        </div>
                 </div>

                <div className="executive-dashboard">
                    {!hasData ? (
                        <p className="text-muted mt-3">{t('table.notAvailable')}</p>
                    ) : (
                    <div
                        className="table-responsive list-table table-scroll"
                        style={{ maxHeight: 'calc(100vh - 220px)', overflowY: 'auto' }}
                        ref={tableContainerRef}
                    >
                        <table
                            className="table table-default table-bordered"
                            style={{ tableLayout: 'fixed', width: 'max-content', marginBottom: 0 }}
                        >
                            <colgroup>
                                <col style={{ width: '150px' }} />
                                {monthLabels.map((_, i) => (
                                    <React.Fragment key={i}>
                                        <col style={{ width: '62px' }} />
                                        <col style={{ width: '68px' }} />
                                        <col style={{ width: '72px' }} />
                                        <col style={{ width: '52px' }} />
                                    </React.Fragment>
                                ))}
                            </colgroup>
                            <thead>
                                <tr>
                                    <th
                                        className="text-start"
                                        style={{ position: 'sticky', top: 0, zIndex: 3, background: '#1560C5', padding: '6px 8px', verticalAlign: 'middle' }}
                                    >
                                        {t('table.financialRow')}
                                    </th>
                                    {monthLabels.map((label, i) => (
                                        <React.Fragment key={i}>
                                            <th
                                                className="text-center"
                                                style={{ position: 'sticky', top: 0, zIndex: 3, background: '#1560C5', padding: '4px 3px', whiteSpace: 'normal', lineHeight: '1.3', borderLeft: '2px solid #3a7bd5', verticalAlign: 'middle' }}
                                            >
                                                <div style={{ fontSize: '10px', fontWeight: 400, opacity: 0.85 }}>{label}</div>
                                                <div style={{ textDecoration: 'underline' }}>{t('table.amount')}</div>
                                            </th>
                                            <th className="text-center" style={{ position: 'sticky', top: 0, zIndex: 3, background: '#1560C5', padding: '4px 3px', whiteSpace: 'normal', lineHeight: '1.3', verticalAlign: 'bottom' }}>
                                                {t('table.budgetAmount')}
                                            </th>
                                            <th className="text-center" style={{ position: 'sticky', top: 0, zIndex: 3, background: '#1560C5', padding: '4px 3px', whiteSpace: 'normal', lineHeight: '1.3', verticalAlign: 'bottom' }}>
                                                {t('table.overBudget')}
                                            </th>
                                            <th className="text-center" style={{ position: 'sticky', top: 0, zIndex: 3, background: '#1560C5', padding: '4px 3px', whiteSpace: 'normal', lineHeight: '1.3', verticalAlign: 'bottom' }}>
                                                {t('table.pctOfBudget')}
                                            </th>
                                        </React.Fragment>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {reportData.length > 0 ? (
                                    reportData.map((row, idx) => {
                                        const { report_code } = row.account_chart;
                                        const isHeader = ['GH', 'SH'].includes(report_code);
                                        const fw = ['GH', 'GT'].includes(report_code) ? 700
                                            : ['SH', 'ST', 'SSH', 'SST'].includes(report_code) ? 600
                                            : 400;

                                        return (
                                            <tr
                                                key={idx}
                                                className={`bs-table-body-${report_code}`}
                                                style={{ height: '26px' }}
                                            >
                                                <td style={{ padding: '2px 8px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', verticalAlign: 'middle', fontWeight: fw }}>
                                                    {!isHeader && row.account_chart.account ? `${row.account_chart.account} ` : ''}{row.account_chart.name}
                                                </td>
                                                {isHeader ? (
                                                    <td colSpan={colSpanCount || 4}></td>
                                                ) : (
                                                    row.months.map((m, mi) => (
                                                        <React.Fragment key={mi}>
                                                            <td className="text-end" style={{ padding: '2px 4px', verticalAlign: 'middle', fontWeight: fw, borderLeft: mi === 0 ? '2px solid #dee2e6' : undefined }}>{formatAmount(m.actual)}</td>
                                                            <td className="text-end" style={{ padding: '2px 4px', verticalAlign: 'middle', fontWeight: fw }}>{formatAmount(m.budget)}</td>
                                                            <td className="text-end" style={{ padding: '2px 4px', verticalAlign: 'middle', fontWeight: fw }}>{formatAmount(m.over_budget)}</td>
                                                            <td className="text-end" style={{ padding: '2px 4px', verticalAlign: 'middle', fontWeight: fw }}>{formatPct(m.pct_of_budget)}</td>
                                                        </React.Fragment>
                                                    ))
                                                )}
                                            </tr>
                                        );
                                    })
                                ) : (
                                    <tr style={{ height: '32px' }}>
                                        <td colSpan={Math.max(colSpanCount + 1, 5)} className="text-center">
                                            <span>{t('table.notAvailable')}</span>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                    )}
                    <ScrollToggle scrollRef={tableContainerRef} />
                </div>
            </div>
        </div>
    );
};

export default BudgetVsActual;
